/**
 * ChangePasswordPage
 * Handles both:
 *  1. FORCED change (expired / first login) — redirected here from login
 *  2. VOLUNTARY change (user-initiated from profile)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ShieldCheck, ArrowLeft, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button }   from '../../components/ui/button';
import { Input }    from '../../components/ui/input';
import { Label }    from '../../components/ui/label';
import { Badge }    from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { useAuth }  from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { API_BASE, API } from '../../lib/api';


export default function ChangePasswordPage() {
  const { accessToken, userSummary, logout } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const state    = location.state || {};
  const reason   = state.reason || 'voluntary';      // expired | first_login | voluntary
  const isForced = reason === 'expired' || reason === 'first_login';

  const [currentPwd, setCurrentPwd]   = useState('');
  const [newPwd, setNewPwd]           = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');
  const [showCurr, setShowCurr]       = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConf, setShowConf]       = useState(false);
  const [loading, setLoading]         = useState(false);
  const [done, setDone]               = useState(false);

  useEffect(() => {
    if (!accessToken) navigate('/login');
  }, [accessToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match.'); return; }
    if (newPwd.length < 6) { toast.error('Password too short.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: isForced ? undefined : currentPwd, new_password: newPwd, confirm_password: confirmPwd },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toast.success('Password changed successfully!');
      setDone(true);
      setTimeout(() => navigate('/select-app'), 2000);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      const details = err.response?.data?.details;
      toast.error(msg || 'Failed to change password.');
      if (details?.length) details.forEach(d => toast.error(d, { duration: 6000 }));
    } finally { setLoading(false); }
  };

  const REASON_META = {
    expired:     { badge: 'Password Expired',    color: '#FF5A6A', msg: 'Your password has expired. You must set a new password to continue.' },
    first_login: { badge: 'First Login',         color: '#F5C451', msg: 'For security, please set a new password before proceeding.' },
    voluntary:   { badge: '21 CFR Compliance',   color: '#3DD56D', msg: 'Change your password to maintain account security.' },
  };
  const meta = REASON_META[reason] || REASON_META.voluntary;

  return (
    <div className="min-h-screen dark:bg-[#080808] bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 dark:border-b dark:border-white/[0.06] border-b border-gray-100">
        <div className="flex items-center gap-3">
          {!isForced && (
            <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-lg dark:bg-white/[0.05] bg-white border dark:border-white/[0.09] border-gray-200 flex items-center justify-center dark:text-neutral-400 text-gray-500 dark:hover:text-white hover:text-gray-900 transition-colors">
              <ArrowLeft size={14} />
            </button>
          )}
          <RetailCliqLogo size="sm" />
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {isForced && (
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="text-xs dark:text-neutral-500 text-gray-400 h-8 gap-1.5">
              <LogOut size={12} /> Sign Out
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-5">
        <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ duration:.35 }} className="w-full max-w-md">

          {done ? (
            <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#3DD56D]/10 border-2 border-[#3DD56D]/25 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={30} className="text-[#3DD56D]" />
              </div>
              <h2 className="text-lg font-bold dark:text-white text-gray-900 mb-1" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>Password Changed!</h2>
              <p className="dark:text-neutral-400 text-gray-500 text-sm">Redirecting to your applications...</p>
            </div>
          ) : (
            <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold dark:text-white text-gray-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>Change Password</h2>
                  <p className="dark:text-neutral-400 text-gray-500 text-xs mt-1">{meta.msg}</p>
                </div>
                <Badge className="text-[10px] px-2 py-0.5 flex-shrink-0" style={{ backgroundColor:`${meta.color}18`, color:meta.color, borderColor:`${meta.color}30`, border:'1px solid' }}>
                  {meta.badge}
                </Badge>
              </div>

              {/* User info */}
              {userSummary && (
                <div className="flex items-center gap-2.5 mb-5 px-3 py-2.5 dark:bg-white/[0.03] bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-[#3DD56D] flex items-center justify-center text-black font-bold text-xs flex-shrink-0">{userSummary.avatar_initials}</div>
                  <div>
                    <p className="dark:text-white text-gray-900 text-sm font-medium">{userSummary.name}</p>
                    <p className="dark:text-neutral-500 text-gray-400 text-xs">{userSummary.role} · {userSummary.company}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Current password (not required for forced changes) */}
                {!isForced && (
                  <div className="space-y-1.5">
                    <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">Current Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13} />
                      <Input type={showCurr?'text':'password'} placeholder="Your current password" value={currentPwd} onChange={e=>setCurrentPwd(e.target.value)}
                        className="pl-9 pr-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                      <button type="button" onClick={()=>setShowCurr(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 hover:text-gray-600 transition-colors">{showCurr?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                    </div>
                  </div>
                )}

                {/* New password */}
                <div className="space-y-1.5">
                  <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13} />
                    <Input type={showNew?'text':'password'} placeholder="Create a strong password" value={newPwd} onChange={e=>setNewPwd(e.target.value)}
                      className="pl-9 pr-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                    <button type="button" onClick={()=>setShowNew(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 hover:text-gray-600 transition-colors">{showNew?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                  </div>
                  <PasswordStrengthMeter password={newPwd} userType={userSummary?.role?.includes('ADMIN')?'ADMIN':'M'} />
                </div>

                {/* Confirm password */}
                <div className="space-y-1.5">
                  <Label className="text-xs dark:text-neutral-300 text-gray-700 font-medium">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13} />
                    <Input type={showConf?'text':'password'} placeholder="Repeat new password" value={confirmPwd} onChange={e=>setConfirmPwd(e.target.value)}
                      className={`pl-9 pr-9 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 focus-visible:ring-1 ${confirmPwd && newPwd !== confirmPwd ? 'border-[#FF5A6A] focus-visible:ring-[#FF5A6A]' : 'dark:border-white/[0.09] border-gray-200 focus-visible:ring-[#3DD56D]'}`} />
                    <button type="button" onClick={()=>setShowConf(v=>!v)} className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 hover:text-gray-600 transition-colors">{showConf?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                  </div>
                  {confirmPwd && newPwd !== confirmPwd && <p className="text-[11px] text-[#FF5A6A]">Passwords do not match</p>}
                </div>

                <Separator className="dark:bg-white/[0.07] bg-gray-100" />

                {/* 21 CFR compliance note */}
                <div className="dark:bg-[#3DD56D]/[0.05] bg-[#3DD56D]/[0.05] border dark:border-[#3DD56D]/15 border-[#3DD56D]/15 rounded-xl px-3 py-2.5">
                  <p className="text-[10px] dark:text-neutral-400 text-gray-500 leading-relaxed">
                    <span className="font-semibold text-[#3DD56D]">21 CFR Part 11</span> — Password history of last 3 changes is maintained.
                    You cannot reuse recent passwords. Strong passwords are enforced per your account policy.
                  </p>
                </div>

                <Button type="submit" disabled={loading || (newPwd !== confirmPwd && !!confirmPwd)} className="w-full h-9 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_4px_16px_rgba(61,213,109,0.24)] disabled:opacity-50 text-sm transition-colors">
                  {loading ? <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin"/>Changing...</span> : 'Change Password'}
                </Button>
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
